import { fold } from '@/domain';

/**
 * Which rows of a searched list answer what was typed, and in what order. The
 * request of 6 October 2026 (fifth), section 2: every dropdown of an address
 * is searchable, by any part of a name and with or without its accents.
 */

/** A row as the search reads one: its words, and whatever else it is found by. */
export interface Matchable {
  label: string;
  /** More the row is found by beside its label: a country's code, `+971`. */
  alsoFoundBy?: readonly string[];
}

/** How well a row answers what was typed, already folded: the lower the better, null for not at all. */
function rank(option: Matchable, typed: string): number | null {
  const label = fold(option.label);
  const others = (option.alsoFoundBy ?? []).map((word) => fold(word));

  if (label === typed) return 0;
  // The name begins with it, or it is the row's code itself: "ae", "+971".
  if (label.startsWith(typed) || others.includes(typed)) return 1;
  // A word of the name begins with it: "arab" finds United Arab Emirates before Saudi Arabia.
  if (label.split(/[\s\-'(]+/).some((word) => word.startsWith(typed))) return 2;
  if (others.some((word) => word.startsWith(typed))) return 2;
  return label.includes(typed) || others.some((word) => word.includes(typed)) ? 3 : null;
}

/**
 * The rows that answer what was typed, the best first and otherwise in the
 * order they came. Compared folded, so `sao` finds São Paulo and `EMIR` finds
 * United Arab Emirates. Nothing typed is every row.
 */
export function narrow<Row extends Matchable>(options: readonly Row[], typed: string): readonly Row[] {
  const wanted = fold(typed);
  if (wanted === '') return options;

  return options
    .map((option, index) => ({ option, index, rank: rank(option, wanted) }))
    .filter((each): each is { option: Row; index: number; rank: number } => each.rank !== null)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((each) => each.option);
}
